// packages/frontend/src/services/authService.ts

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updatePassword,
  updateProfile,
  User,
  UserCredential,
  AuthError as FirebaseAuthError,
  sendEmailVerification,
  ActionCodeSettings,
  updateEmail,
  signInWithPopup,
  GoogleAuthProvider,
} from 'firebase/auth';
import { auth } from './firebase';
import { logger } from '../utils/logger';

// Custom error types for better error handling
export class AuthError extends Error {
  constructor(
    message: string,
    public code: string,
    public originalError?: FirebaseAuthError
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

export class ValidationError extends Error {
  constructor(message: string, public field: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

// Input validation utilities
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }

  if (!/(?=.*[a-z])/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (!/(?=.*[A-Z])/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (!/(?=.*\d)/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Error mapping for user-friendly messages
const getAuthErrorMessage = (errorCode: string): string => {
  const errorMessages: Record<string, string> = {
    'auth/user-not-found': 'No account found with this email address',
    'auth/wrong-password': 'Incorrect password',
    'auth/email-already-in-use': 'An account with this email already exists',
    'auth/weak-password': 'Password is too weak. Please choose a stronger password',
    'auth/invalid-email': 'Please enter a valid email address',
    'auth/user-disabled': 'This account has been disabled',
    'auth/too-many-requests': 'Too many failed attempts. Please try again later',
    'auth/network-request-failed': 'Network error. Please check your connection',
    'auth/requires-recent-login': 'Please log in again to perform this action',
    'auth/invalid-credential': 'Invalid credentials provided',
    'auth/operation-not-allowed': 'This operation is not allowed',
    'auth/account-exists-with-different-credential': 'An account already exists with this email',
  };

  return errorMessages[errorCode] || 'An authentication error occurred';
};

/**
 * Sign in with email and password
 */
export const loginWithEmail = async (
  email: string,
  password: string
): Promise<User> => {
  try {
    // Validate inputs
    if (!email || !password) {
      throw new ValidationError('Email and password are required', 'credentials');
    }

    if (!validateEmail(email)) {
      throw new ValidationError('Please enter a valid email address', 'email');
    }

    // Attempt to sign in
    const userCredential: UserCredential = await signInWithEmailAndPassword(
      auth,
      email.trim().toLowerCase(),
      password
    );

    return userCredential.user;
  } catch (error) {
    if (error instanceof ValidationError) {
      throw error;
    }

    const authError = error as FirebaseAuthError;
    const message = getAuthErrorMessage(authError.code);
    throw new AuthError(message, authError.code, authError);
  }
};

// Page the verification link returns the user to, and the query flag that marks a return
const VERIFY_EMAIL_PATH = '/verify-email';
const VERIFIED_RETURN_QUERY = 'verified=1';

/**
 * Action-code settings for verification emails. The continue URL is built from
 * the app's own origin so Firebase's hosted page shows a Continue button that
 * brings the user back here. It carries no user data.
 */
export const getEmailVerificationActionCodeSettings = (): ActionCodeSettings => ({
  url: `${window.location.origin}${VERIFY_EMAIL_PATH}?${VERIFIED_RETURN_QUERY}`,
  handleCodeInApp: false,
});

/**
 * Create a new user account with email and password
 */
export const registerWithEmail = async (
  email: string,
  password: string,
  displayName?: string
): Promise<User> => {
  try {
    // Validate inputs
    if (!email || !password) {
      throw new ValidationError('Email and password are required', 'credentials');
    }

    if (!validateEmail(email)) {
      throw new ValidationError('Please enter a valid email address', 'email');
    }

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      throw new ValidationError(
        passwordValidation.errors.join('. '),
        'password'
      );
    }

    // Create user account
    const userCredential: UserCredential = await createUserWithEmailAndPassword(
      auth,
      email.trim().toLowerCase(),
      password
    );

    // Update profile if display name provided
    if (displayName) {
      await updateProfile(userCredential.user, {
        displayName: displayName.trim(),
      });
    }

    // Send email verification
    await sendEmailVerification(userCredential.user, getEmailVerificationActionCodeSettings());

    return userCredential.user;
  } catch (error) {
    if (error instanceof ValidationError) {
      throw error;
    }

    const authError = error as FirebaseAuthError;
    const message = getAuthErrorMessage(authError.code);
    throw new AuthError(message, authError.code, authError);
  }
};

/**
 * Sign out the current user
 */
export const logoutUser = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error) {
    const authError = error as FirebaseAuthError;
    const message = getAuthErrorMessage(authError.code);
    throw new AuthError(message, authError.code, authError);
  }
};

/**
 * Resend the email verification link to the currently signed-in user.
 */
export const resendVerificationEmail = async (): Promise<void> => {
  try {
    if (!auth.currentUser) {
      throw new AuthError('No signed-in user', 'auth/no-current-user');
    }

    await sendEmailVerification(auth.currentUser, getEmailVerificationActionCodeSettings());
  } catch (error) {
    if (error instanceof AuthError) {
      throw error;
    }

    const authError = error as FirebaseAuthError;
    const message = getAuthErrorMessage(authError.code);
    throw new AuthError(message, authError.code, authError);
  }
};

/**
 * Reload the currently signed-in user from Firebase and force an ID token
 * refresh, then return the up-to-date `emailVerified` flag. A force refresh
 * is required because the token's `email_verified` claim is only updated on
 * mint, not by `reload()` alone.
 */
export const checkEmailVerified = async (): Promise<boolean> => {
  if (!auth.currentUser) {
    return false;
  }

  try {
    await auth.currentUser.reload();
    await auth.currentUser.getIdToken(true);
    return auth.currentUser?.emailVerified ?? false;
  } catch (error) {
    const authError = error as FirebaseAuthError;
    const message = getAuthErrorMessage(authError.code);
    throw new AuthError(message, authError.code, authError);
  }
};

/**
 * Send password reset email
 */
export const resetPassword = async (email: string): Promise<void> => {
  try {
    if (!email) {
      throw new ValidationError('Email is required', 'email');
    }

    if (!validateEmail(email)) {
      throw new ValidationError('Please enter a valid email address', 'email');
    }

    await sendPasswordResetEmail(auth, email.trim().toLowerCase());
  } catch (error) {
    if (error instanceof ValidationError) {
      throw error;
    }

    const authError = error as FirebaseAuthError;
    const message = getAuthErrorMessage(authError.code);
    throw new AuthError(message, authError.code, authError);
  }
};

/**
 * Update user password
 */
export const updateUserPassword = async (newPassword: string): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new AuthError('No user is currently signed in', 'auth/no-user');
    }

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      throw new ValidationError(
        passwordValidation.errors.join('. '),
        'password'
      );
    }

    await updatePassword(user, newPassword);
  } catch (error) {
    if (error instanceof ValidationError) {
      throw error;
    }

    const authError = error as FirebaseAuthError;
    const message = getAuthErrorMessage(authError.code);
    throw new AuthError(message, authError.code, authError);
  }
};

/**
 * Update user profile
 */
export const updateUserProfile = async (updates: {
  displayName?: string;
  photoURL?: string;
}): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new AuthError('No user is currently signed in', 'auth/no-user');
    }

    await updateProfile(user, updates);
  } catch (error) {
    const authError = error as FirebaseAuthError;
    const message = getAuthErrorMessage(authError.code);
    throw new AuthError(message, authError.code, authError);
  }
};

/**
 * Update user email
 */
export const updateUserEmail = async (newEmail: string): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new AuthError('No user is currently signed in', 'auth/no-user');
    }

    if (!validateEmail(newEmail)) {
      throw new ValidationError('Please enter a valid email address', 'email');
    }

    await updateEmail(user, newEmail.trim().toLowerCase());
  } catch (error) {
    if (error instanceof ValidationError) {
      throw error;
    }

    const authError = error as FirebaseAuthError;
    const message = getAuthErrorMessage(authError.code);
    throw new AuthError(message, authError.code, authError);
  }
};

/**
 * Get the current user
 */
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!auth.currentUser;
};

/**
 * Get user's ID token
 */
export const getIdToken = async (forceRefresh: boolean = false): Promise<string> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new AuthError('No user is currently signed in', 'auth/no-user');
    }

    return await user.getIdToken(forceRefresh);
  } catch (error) {
    // Our own AuthError already carries a precise message; re-mapping it through
    // getAuthErrorMessage() would replace it with the generic fallback, since
    // codes like 'auth/no-user' are not in that map.
    if (error instanceof AuthError) {
      throw error;
    }

    const authError = error as FirebaseAuthError;
    const message = getAuthErrorMessage(authError.code);
    throw new AuthError(message, authError.code, authError);
  }
};

/**
 * Sign in with Google
 */
export const loginWithGoogle = async (): Promise<User> => {
  try {
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');

    // Enhanced logging for production debugging
    logger.info('Starting Google authentication', {
      environment: import.meta.env.MODE,
      domain: window.location.origin,
      authDomain: auth.config.authDomain,
    });

    const result = await signInWithPopup(auth, provider);
    
    logger.info('Google authentication successful', {
      userId: result.user.uid,
      email: result.user.email,
    });
    
    return result.user;
  } catch (error) {
    const authError = error as FirebaseAuthError;
    const message = getAuthErrorMessage(authError.code);
    
    // Enhanced error logging for production debugging
    logger.error('Google authentication failed', {
      error: authError,
      code: authError.code,
      message: authError.message,
      environment: import.meta.env.MODE,
      domain: window.location.origin,
      authDomain: auth.config.authDomain,
    });
    
    throw new AuthError(message, authError.code, authError);
  }
};



// Export the auth instance for direct use if needed
export { auth };
